/**
 * MetricCard Component
 *
 * A dashboard metric card component for displaying key performance indicators.
 * Supports icons, trends, and hover effects.
 */

import React, { forwardRef } from 'react';
import { cn } from '../../utils/cn';

export type MetricCardVariant = 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info';
export type MetricTrend = 'up' | 'down' | 'neutral';
export type TrendType = 'positive' | 'negative' | 'neutral';

export interface MetricCardProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string;
  value: string | number;
  trend?: string;
  trendType?: TrendType;
  icon?: string;
  variant?: MetricCardVariant;
  loading?: boolean;
}

const MetricCard = forwardRef<HTMLDivElement, MetricCardProps>(
  (
    {
      title,
      value,
      trend,
      trendType = 'neutral',
      icon,
      variant = 'default',
      loading = false,
      className,
      ...props
    },
    ref
  ) => {
    const baseClasses = cn(
      'bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700',
      'shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1',
      'cursor-pointer'
    );

    const variantClasses = {
      default: 'border-gray-200 dark:border-gray-700',
      primary: 'border-primary-200 dark:border-primary-700 bg-primary-50 dark:bg-primary-900/50',
      success: 'border-success-200 dark:border-success-700 bg-success-50 dark:bg-success-900/50',
      warning: 'border-warning-200 dark:border-warning-700 bg-warning-50 dark:bg-warning-900/50',
      error: 'border-error-300 dark:border-error-600 bg-error-50 dark:bg-error-900/50',
      info: 'border-info-200 dark:border-info-700 bg-info-50 dark:bg-info-900/50',
    };

    const iconClasses = {
      default: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
      primary: 'bg-primary-100 dark:bg-primary-800 text-primary-600 dark:text-primary-300',
      success: 'bg-success-100 dark:bg-success-800 text-success-600 dark:text-success-300',
      warning: 'bg-warning-100 dark:bg-warning-800 text-warning-600 dark:text-warning-300',
      error: 'bg-error-100 dark:bg-error-800 text-error-600 dark:text-error-300',
      info: 'bg-info-100 dark:bg-info-800 text-info-600 dark:text-info-300',
    };

    const trendClasses = {
      positive: 'text-success-600 dark:text-success-400',
      negative: 'text-error-600 dark:text-error-400',
      neutral: 'text-gray-700 dark:text-gray-400',
    };

    const trendIcons = {
      positive: '↗',
      negative: '↘',
      neutral: '→',
    };

    if (loading) {
      return (
        <div ref={ref} className={cn(baseClasses, className)} {...props}>
          <div className="animate-pulse">
            <div className="flex items-center justify-between mb-4">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24"></div>
              <div className="h-10 w-10 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
            </div>
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-16 mb-2"></div>
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-20"></div>
          </div>
        </div>
      );
    }

    return (
      <div ref={ref} className={cn(baseClasses, variantClasses[variant], className)} {...props}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-400 uppercase tracking-wide">
            {title}
          </h3>
          {icon && (
            <div
              className={cn(
                'w-10 h-10 rounded-lg flex items-center justify-center',
                iconClasses[variant]
              )}
            >
              <span className="material-icons text-lg">{icon}</span>
            </div>
          )}
        </div>

        <div className="text-3xl font-bold text-gray-900 dark:text-white mb-2">{value}</div>

        {trend && (
          <div className={cn('flex items-center text-sm font-medium', trendClasses[trendType])}>
            <span className="mr-1">{trendIcons[trendType]}</span>
            {trend}
          </div>
        )}
      </div>
    );
  }
);

MetricCard.displayName = 'MetricCard';

export { MetricCard };
